import React, { useState, useCallback, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { RotateCcw, ChevronRight } from 'lucide-react';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const pop = keyframes`0%{transform:scale(0.85)}60%{transform:scale(1.1)}100%{transform:scale(1)}`;
const shake = keyframes`0%,100%{transform:translateX(0)}25%{transform:translateX(-7px)}75%{transform:translateX(7px)}`;
const jump = keyframes`0%{transform:translateY(0)}40%{transform:translateY(-18px)}70%{transform:translateY(-6px)}100%{transform:translateY(0)}`;
const confettiFall = keyframes`
  0%{opacity:1;transform:translate(0,0)rotate(0deg)}
  100%{opacity:0;transform:translate(var(--dx),130px)rotate(720deg)}
`;
const glow = keyframes`
  0%,100%{box-shadow:0 0 0 rgba(102,126,234,0)}
  50%{box-shadow:0 0 24px rgba(102,126,234,0.5)}
`;

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`font-family:'Segoe UI',sans-serif;animation:${fadeIn} 0.4s ease;`;

const SentenceDisplay = styled.div`
  background:linear-gradient(135deg,#667eea,#764ba2);
  border-radius:18px; padding:22px 24px; margin-bottom:16px;
  min-height:68px; text-align:center;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  position:relative;
`;

const SentenceLabel = styled.div`
  font-size:10px;font-weight:700;color:rgba(255,255,255,0.7);
  text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;
`;

const MeaningText = styled.div`
  font-size:20px;font-weight:700;color:#fff;line-height:1.4;
`;

const BuildArea = styled.div<{ state: 'idle'|'correct'|'wrong' }>`
  min-height:60px;border:2.5px dashed;border-radius:16px;
  padding:12px 16px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;
  margin-bottom:16px;transition:all 0.2s;background:#fff;

  ${({ state }) => {
    if (state === 'idle')    return css`border-color:#c7d2fe;`;
    if (state === 'correct') return css`border-color:#22c55e;background:#f0fdf4;animation:${glow} 0.5s ease;`;
    if (state === 'wrong')   return css`border-color:#ef4444;background:#fff1f2;animation:${shake} 0.35s ease;`;
  }}
`;

const BuildLabel = styled.div`
  font-size:11px;color:#94a3b8;font-weight:600;
  letter-spacing:1px;text-transform:uppercase;
  width:100%;
`;

const WordToken = styled.button<{ jumping?: boolean }>`
  padding:5px 11px;border-radius:22px;border:none;
  font-size:16px;font-weight:700;cursor:pointer;
  transition:all 0.18s;
  ${({ jumping }) => jumping ? css`animation:${jump} 0.5s ease;` : ''}

  @media(min-width:768px){
    padding:13px 24px;
    font-size:20px;
    border-radius:32px;
  }
`;

const BuiltToken = styled(WordToken)`
  background:linear-gradient(90deg,#667eea,#764ba2);
  color:#fff;
  box-shadow:0 3px 10px rgba(102,126,234,0.3);
  &:hover{opacity:0.85;transform:translateY(-2px);}
`;

const PoolToken = styled(WordToken)`
  background:#eef2ff;color:#3730a3;border:2px solid #c7d2fe;
  &:hover{background:#667eea;color:#fff;border-color:#667eea;transform:translateY(-2px);}
`;

const PoolArea = styled.div`
  display:flex;flex-wrap:wrap;gap:8px;margin-bottom:20px;min-height:48px;
  padding:12px;background:#f8faff;border-radius:14px;border:1.5px solid #e2e8f0;

  @media(min-width:768px){
    gap:12px;
    padding:16px;
    min-height:72px;
  }
`;

const FeedbackBar = styled.div<{ correct: boolean }>`
  border-radius:12px;padding:14px 18px;margin-bottom:14px;
  font-size:15px;font-weight:700;display:flex;gap:10px;align-items:flex-start;
  animation:${pop} 0.35s ease;
  ${p => p.correct
    ? css`background:#dcfce7;border:2px solid #22c55e;color:#166534;`
    : css`background:#fee2e2;border:2px solid #ef4444;color:#991b1b;`
  }
`;

const ActionRow = styled.div`display:flex;gap:10px;justify-content:center;flex-wrap:wrap;margin-bottom:8px;`;

const PrimaryBtn = styled.button`
  background:linear-gradient(90deg,#667eea,#764ba2);color:#fff;
  border:none;border-radius:12px;padding:12px 28px;
  font-size:14px;font-weight:700;cursor:pointer;
  display:flex;align-items:center;gap:8px;
  transition:all 0.2s;
  &:hover{opacity:0.9;transform:translateY(-2px);}
  &:disabled{opacity:0.4;cursor:not-allowed;transform:none;}
`;

const SecBtn = styled.button`
  background:#fff;color:#64748b;
  border:2px solid #e2e8f0;border-radius:12px;padding:12px 20px;
  font-size:14px;font-weight:600;cursor:pointer;
  display:flex;align-items:center;gap:6px;
  transition:all 0.2s;
  &:hover{border-color:#94a3b8;color:#334155;}
`;

const Progress = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  margin-bottom:12px;font-size:13px;color:#64748b;font-weight:600;
`;

const ProgBar = styled.div<{ pct: number }>`
  height:6px;border-radius:3px;background:#e2e8f0;
  margin-bottom:18px;overflow:hidden;
  &::after{content:'';display:block;height:100%;
    width:${p => p.pct}%;
    background:linear-gradient(90deg,#667eea,#a78bfa);
    border-radius:3px;transition:width 0.5s ease;}
`;

const ResultBox = styled.div`
  text-align:center;padding:40px 24px;
  background:#fff;border-radius:20px;
  box-shadow:0 4px 24px rgba(0,0,0,0.08);
`;

const Confetti = styled.div<{ color:string; delay:number; left:number }>`
  position:fixed;top:0;left:${p=>p.left}vw;
  width:8px;height:8px;background:${p=>p.color};border-radius:2px;
  pointer-events:none;z-index:9999;
  animation:${confettiFall} ${()=>1.2+Math.random()*0.8}s linear ${p=>p.delay}ms forwards;
  --dx:${()=>(Math.random()-0.5)*180}px;
`;

// ─── Sound ───────────────────────────────────────────────────────────────────

function playBeep(freq: number, dur: number, type: OscillatorType = 'sine') {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g); g.connect(ctx.destination);
    osc.frequency.value = freq; osc.type = type;
    g.gain.setValueAtTime(0.35, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.start(); osc.stop(ctx.currentTime + dur);
  } catch {}
}

const playClick = () => playBeep(660, 0.08);
const playCorrect = () => { playBeep(880, 0.12); setTimeout(() => playBeep(1320, 0.14), 100); };
const playWrong = () => playBeep(200, 0.4, 'sawtooth');
const playComplete = () => { [523,659,784,1047].forEach((f,i)=>setTimeout(()=>playBeep(f,0.2),i*80)); };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]]; }
  return a;
}

function sentenceToTokens(sentence: string): string[] {
  // Tokenize: split on spaces/punctuation but keep words & punctuation as separate tokens
  return sentence.match(/[\w']+|[.,!?;:]/g) || sentence.split(' ');
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  sentenceItems: any[];
  onGameComplete?: (pct: number) => void;
}

interface TokenItem { word: string; uid: string; }

interface Question {
  meaning: string;   // Vietnamese (back)
  sentence: string;  // English original (front)
  tokens: string[];  // correct order
  pool: TokenItem[]; // shuffled pool
}

const ReviewSortGame: React.FC<Props> = ({ sentenceItems, onGameComplete }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [qIdx, setQIdx] = useState(0);
  const [built, setBuilt] = useState<TokenItem[]>([]);
  const [pool, setPool] = useState<TokenItem[]>([]);
  const [feedState, setFeedState] = useState<'idle'|'correct'|'wrong'>('idle');
  const [feedback, setFeedback] = useState('');
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [jumpingId, setJumpingId] = useState<string|null>(null);

  const init = useCallback(() => {
    if (!sentenceItems.length) return;
    const qs: Question[] = shuffle(sentenceItems).map(item => {
      const tokens = sentenceToTokens(item.front);
      const pool = shuffle(tokens.map((word, i) => ({ word, uid: `${i}-${word}` })));
      return { meaning: item.back, sentence: item.front, tokens, pool };
    });
    setQuestions(qs);
    setQIdx(0);
    setBuilt([]);
    setPool(qs[0]?.pool || []);
    setFeedState('idle'); setFeedback('');
    setAnswered(false); setScore(0); setDone(false); setShowConfetti(false);
  }, [sentenceItems]);

  useEffect(() => { init(); }, [init]);

  // Report result when game ends
  useEffect(() => {
    if (done && questions.length > 0 && onGameComplete) {
      onGameComplete(Math.round((score / questions.length) * 100));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const loadQ = useCallback((idx: number, qs: Question[]) => {
    setQIdx(idx);
    setBuilt([]);
    setPool(qs[idx].pool);
    setFeedState('idle'); setFeedback('');
    setAnswered(false);
  }, []);

  // Pick from pool → add to built
  const pickFromPool = (token: TokenItem) => {
    if (answered) return;
    playClick();
    setJumpingId(token.uid);
    setTimeout(() => setJumpingId(null), 500);
    setPool(p => p.filter(t => t.uid !== token.uid));
    setBuilt(b => [...b, token]);
  };

  // Remove from built → back to pool
  const removeFromBuilt = (token: TokenItem) => {
    if (answered) return;
    playClick();
    setBuilt(b => b.filter(t => t.uid !== token.uid));
    setPool(p => [...p, token]);
  };

  const check = () => {
    const q = questions[qIdx];
    // Compare token arrays instead of full strings to avoid
    // spacing issues with punctuation (e.g. "chess ." vs "chess.")
    const builtTokens = built.map(t => t.word.toLowerCase());
    const answerTokens = q.tokens.map(t => t.toLowerCase());
    const correct = builtTokens.length === answerTokens.length &&
      builtTokens.every((w, i) => w === answerTokens[i]);

    if (correct) {
      playCorrect();
      setFeedState('correct');
      setFeedback(`✅ Chính xác! "${q.sentence}"`);
      setScore(s => s + 1);
    } else {
      playWrong();
      setFeedState('wrong');
      setFeedback(`❌ Chưa đúng! Câu đúng là: "${q.sentence}"`);
    }
    setAnswered(true);
  };

  const next = () => {
    if (qIdx + 1 >= questions.length) {
      setDone(true);
      playComplete();
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    } else {
      loadQ(qIdx + 1, questions);
    }
  };

  const tryAgain = () => {
    const q = questions[qIdx];
    setBuilt([]);
    setPool(q.pool);
    setFeedState('idle'); setFeedback('');
    setAnswered(false);
  };

  if (!sentenceItems.length) return (
    <div style={{ textAlign:'center', color:'#bbb', padding:40 }}>
      Cần có mẫu câu trong bài ôn tập để chơi trò sắp xếp câu.
    </div>
  );

  if (!questions.length) return null;

  const q = questions[qIdx];

  if (done) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div>
        {showConfetti && Array.from({ length: 28 }).map((_, i) => (
          <Confetti key={i} color={['#667eea','#f59e0b','#22c55e','#ec4899','#38bdf8'][i%5]} delay={i*65} left={Math.random()*100} />
        ))}
        <ResultBox>
          <div style={{ fontSize: 64 }}>{pct>=80?'🏆':pct>=50?'👏':'💪'}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#1a1a2e', margin: '12px 0 4px' }}>
            {score}/{questions.length} câu đúng
          </div>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 24 }}>
            {pct>=80?'Tuyệt vời! Em sắp xếp rất giỏi!':pct>=50?'Tốt lắm! Luyện thêm nhé!':'Cố gắng nhé! Em làm được mà!'}
          </div>
          <button
            onClick={init}
            style={{ background:'linear-gradient(90deg,#667eea,#764ba2)', color:'#fff', border:'none', borderRadius:12, padding:'13px 32px', fontSize: 15, fontWeight: 700, cursor:'pointer' }}
          >
            <RotateCcw size={15} style={{ verticalAlign:'middle', marginRight: 6 }} /> Chơi lại
          </button>
        </ResultBox>
      </div>
    );
  }

  const pct = Math.round((qIdx / questions.length) * 100);

  return (
    <Wrap>
      <Progress>
        <span>Câu {qIdx+1}/{questions.length}</span>
        <span style={{ color:'#22c55e', fontWeight:700 }}>⭐ {score} điểm</span>
      </Progress>
      <ProgBar pct={pct} />

      {/* Vietnamese meaning → user builds English sentence */}
      <SentenceDisplay>
        <SentenceLabel>🇻🇳 Nghĩa tiếng Việt – Sắp xếp câu tiếng Anh</SentenceLabel>
        <MeaningText>{q.meaning}</MeaningText>
      </SentenceDisplay>

      {/* Build area */}
      <BuildArea state={feedState}>
        {built.length === 0 && <BuildLabel>👆 Bấm vào các từ bên dưới để xếp thành câu...</BuildLabel>}
        {built.map(token => (
          <BuiltToken
            key={token.uid}
            jumping={jumpingId === token.uid}
            onClick={() => removeFromBuilt(token)}
          >
            {token.word}
          </BuiltToken>
        ))}
      </BuildArea>

      {/* Word pool */}
      <PoolArea>
        {pool.map(token => (
          <PoolToken
            key={token.uid}
            jumping={false}
            onClick={() => pickFromPool(token)}
            disabled={answered}
          >
            {token.word}
          </PoolToken>
        ))}
        {pool.length === 0 && built.length > 0 && !answered && (
          <div style={{ color: '#94a3b8', fontSize: 13, width: '100%', textAlign: 'center' }}>
            Đã dùng hết từ, bấm kiểm tra!
          </div>
        )}
      </PoolArea>

      {/* Feedback */}
      {feedback && (
        <FeedbackBar correct={feedState === 'correct'}>
          <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>{feedback}</div>
        </FeedbackBar>
      )}

      {/* Action buttons */}
      <ActionRow>
        {!answered ? (
          <>
            <SecBtn onClick={tryAgain} disabled={built.length === 0}>
              <RotateCcw size={14} /> Làm lại
            </SecBtn>
            <PrimaryBtn onClick={check} disabled={built.length === 0}>
              ✔ Kiểm tra
            </PrimaryBtn>
          </>
        ) : (
          <>
            {feedState === 'wrong' && (
              <SecBtn onClick={tryAgain}><RotateCcw size={14} /> Thử lại</SecBtn>
            )}
            <PrimaryBtn onClick={next}>
              {qIdx+1 >= questions.length ? '🏁 Xem kết quả' : 'Câu tiếp'}
              <ChevronRight size={16} />
            </PrimaryBtn>
          </>
        )}
      </ActionRow>
    </Wrap>
  );
};

export default ReviewSortGame;
